/**
 * Channel-specific primary images for the Product Create wizard.
 *
 * One shared product media library, one primary image PER connected account.
 * The Product Master gallery (and its own primary image) is never rewritten by
 * a channel selection. This module only plans selections and applies them
 * through the existing listing-media authority RPC
 * (`set_listing_channel_primary_from_asset`), which sets
 * `media_strategy = 'listing_override'` on THAT listing only.
 *
 * There is no second media engine and no parallel persistence: the selection
 * lives on the draft selling intent (JSONB draft payload) and becomes a real
 * `channel_listing_media` override when the channel draft listing is created.
 */

import { supabase } from "@/lib/supabase";
import type { WizardChannelIntent } from "@/lib/wizard-selling";

export interface WizardMediaOption {
  mediaAssetId: string;
  filename: string | null;
  isPrimary: boolean;
  /** Signed preview URL, presentation only. */
  src?: string | null;
}

export type WizardChannelMediaAuthority = "inherit" | "channel_override" | "missing_override" | "pending_library";

export interface WizardChannelMediaResolution {
  authority: WizardChannelMediaAuthority;
  /** Asset that publication would actually use, or null when nothing is available. */
  mediaAssetId: string | null;
  filename: string | null;
  label: string;
}

function normalizeId(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Select (or clear) one account's primary image. Returns the same array
 * reference when nothing changed so draft autosave is not re-triggered.
 */
export function setWizardChannelPrimaryMedia(
  intents: WizardChannelIntent[],
  connectorId: string,
  mediaAssetId: string | null,
): WizardChannelIntent[] {
  const next = normalizeId(mediaAssetId);
  let changed = false;
  const rows = intents.map((intent) => {
    if (intent.connectorId !== connectorId) return intent;
    if ((intent.primaryMediaAssetId ?? null) === next) return intent;
    changed = true;
    return { ...intent, primaryMediaAssetId: next };
  });
  return changed ? rows : intents;
}

/**
 * Drop channel selections whose asset is no longer in the shared gallery.
 * Those accounts fall back to inherit (safe default) instead of pointing at a
 * deleted asset. Untouched selections and other accounts are left alone.
 */
export function pruneWizardChannelPrimaryMedia(
  intents: WizardChannelIntent[],
  galleryAssetIds: string[],
): { intents: WizardChannelIntent[]; cleared: { connectorId: string; displayName: string; mediaAssetId: string }[] } {
  const live = new Set(galleryAssetIds.map((id) => normalizeId(id)).filter((id): id is string => !!id));
  const cleared: { connectorId: string; displayName: string; mediaAssetId: string }[] = [];
  let changed = false;
  const rows = intents.map((intent) => {
    const chosen = normalizeId(intent.primaryMediaAssetId);
    if (!chosen || live.has(chosen)) return intent;
    changed = true;
    cleared.push({ connectorId: intent.connectorId, displayName: intent.displayName, mediaAssetId: chosen });
    return { ...intent, primaryMediaAssetId: null };
  });
  return { intents: changed ? rows : intents, cleared };
}

/** Which accounts intentionally picked their own image. */
export function channelMediaSelectionCount(intents: WizardChannelIntent[]): { selected: number; overridden: number } {
  const selected = intents.filter((intent) => intent.selected);
  return {
    selected: selected.length,
    overridden: selected.filter((intent) => !!normalizeId(intent.primaryMediaAssetId)).length,
  };
}

/**
 * What this account will actually use. No selection is the safe default:
 * the Product Master primary image (never a silent copy of another account).
 */
export function resolveWizardChannelPrimaryMedia(opts: {
  chosenMediaAssetId?: string | null;
  gallery: WizardMediaOption[];
  canonicalPrimaryMediaAssetId?: string | null;
}): WizardChannelMediaResolution {
  const chosen = normalizeId(opts.chosenMediaAssetId);
  const canonical = normalizeId(opts.canonicalPrimaryMediaAssetId)
    ?? opts.gallery.find((item) => item.isPrimary)?.mediaAssetId
    ?? null;

  if (!chosen) {
    const primary = opts.gallery.find((item) => item.mediaAssetId === canonical)
      ?? opts.gallery.find((item) => item.isPrimary)
      ?? null;
    return {
      authority: "inherit",
      mediaAssetId: primary?.mediaAssetId ?? null,
      filename: primary?.filename ?? null,
      label: primary
        ? `Uses Product Master primary (${primary.filename ?? primary.mediaAssetId})`
        : "Uses the Product Master primary image",
    };
  }

  // An unloaded / empty library cannot prove the chosen asset was deleted, so a
  // stored selection must not be reported as missing. The selection is kept and
  // re-checked once the library arrives and again before the channel draft is
  // created.
  if (opts.gallery.length === 0) {
    return {
      authority: "pending_library",
      mediaAssetId: null,
      filename: null,
      label: "Waiting for the image library — this account's chosen image is kept and checked before the channel draft is created.",
    };
  }

  const asset = opts.gallery.find((item) => item.mediaAssetId === chosen);
  if (!asset) {
    return {
      authority: "missing_override",
      mediaAssetId: null,
      filename: null,
      label: "Selected channel image is no longer in the image library — this account falls back to the Product Master primary until you pick another image.",
    };
  }

  return {
    authority: "channel_override",
    mediaAssetId: asset.mediaAssetId,
    filename: asset.filename,
    label: `Channel primary: ${asset.filename ?? asset.mediaAssetId}`,
  };
}

/**
 * Only selected accounts with a selection that still exists in the gallery are
 * written. Inherit needs no write at all — that is the listing default.
 */
export function wizardChannelMediaApplyPlan(
  intents: WizardChannelIntent[],
  galleryAssetIds: string[],
): { connectorId: string; displayName: string; mediaAssetId: string }[] {
  const live = new Set(galleryAssetIds.map((id) => normalizeId(id)).filter((id): id is string => !!id));
  const plan: { connectorId: string; displayName: string; mediaAssetId: string }[] = [];
  for (const intent of intents) {
    if (!intent.selected) continue;
    const chosen = normalizeId(intent.primaryMediaAssetId);
    if (!chosen || !live.has(chosen)) continue;
    plan.push({ connectorId: intent.connectorId, displayName: intent.displayName, mediaAssetId: chosen });
  }
  return plan;
}

/**
 * Apply one account's chosen primary image to its freshly created draft
 * listing. Uses the existing channel-media authority, so the shared catalogue
 * primary image is untouched and only this listing's media strategy changes.
 */
export async function applyWizardChannelPrimaryMedia(params: {
  orgId: string;
  listingId: string;
  mediaAssetId: string;
  galleryAssetIds: string[];
}): Promise<{ success: boolean; error?: string }> {
  const mediaAssetId = normalizeId(params.mediaAssetId);
  if (!mediaAssetId) return { success: false, error: "No channel image selected" };
  const gallery = new Set(params.galleryAssetIds.map((id) => normalizeId(id)).filter((id): id is string => !!id));
  if (!gallery.has(mediaAssetId)) {
    return { success: false, error: "The selected image is not in the product image gallery" };
  }
  try {
    const { error } = await supabase.rpc("set_listing_channel_primary_from_asset", {
      p_org_id: params.orgId,
      p_listing_id: params.listingId,
      p_media_asset_id: mediaAssetId,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error && error.message ? error.message : "Channel image was not applied",
    };
  }
}